import { useState } from "react";

import { useTranslation } from "react-i18next";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ApiResources } from "@/api/resources";
import useTableItemDetails from "@/components/DetailsSection/hooks/useTableItemDetails";
import { ROUTES } from "@/shared";
import useResponse from "@/shared/hooks/useResponse";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import { IntegrationsRow } from "../types";

export const useDisableIntegration = (
  route: ROUTES,
  payloadApi?: ApiResources
) => {
  const { postData } = useAxiosPrivate();
  const { handleCommonError } = useResponse();
  const { t } = useTranslation();

  const { getTableItemDetails } = useTableItemDetails();

  const { params } = useTableStoreByRoute(route);
  const searchParams = params.contains;

  const [isDisableDialogLoading, setIsDisableDialogLoading] = useState(false);
  const [isDisableDialogOpen, setIsDisableDialogOpen] = useState(false);

  const [switchData, setSwitchData] = useState<IntegrationsRow | null>(null);

  const handleDisableClose = () => {
    setIsDisableDialogOpen(false);
  };

  const handleSubmitDisable = async (rowData: IntegrationsRow) => {
    const { enabled, id } = rowData;
    const nextEnabledState = !enabled;

    const isApiCredentials =
      payloadApi === ApiResources.IntegrationsApiCredentialsEnabled;

    const payload = {
      enabled: nextEnabledState,
      ...(isApiCredentials ? { id } : { integrationId: id })
    };

    const alertAction = enabled ? "disable" : "enable";

    try {
      setIsDisableDialogLoading(true);

      await postData(payloadApi ?? ApiResources.IntegrationsEnabled, payload);

      getTableItemDetails({
        itemId: id,
        successMessage: t(`Alert##${alertAction} integration success`),
        leaveTableDetailsNotUpdated: true,
        valueInSearchField: searchParams ?? undefined
      });

      enabled && setIsDisableDialogOpen(false);
    } catch (error) {
      handleCommonError(error, t);
    } finally {
      setIsDisableDialogLoading(false);
    }
  };

  const handleSwitch = async (rowData: IntegrationsRow) => {
    if (rowData.enabled) {
      setSwitchData(rowData);
      setIsDisableDialogOpen(true);
    } else {
      handleSubmitDisable(rowData);
    }
  };

  return {
    switchData,
    isDisableDialogLoading,
    isDisableDialogOpen,
    handleDisableClose,
    handleSwitch,
    handleSubmitDisable
  };
};
